import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {djangoFetchJson} from 'common/report/queries/utils';
import type {UserList} from 'common/widgets/user-list';
import {MPContext} from 'common/mp-context';
import {fetchDiscoverableOrgs} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';

import {template} from './template';
import css from './index.inline.styl';
import {FullScreenSignupStepId, LAST_STEP} from './types';
import {TrackingEvents} from './tracking';

type FullScreenSignupState = {
  step: FullScreenSignupStepId;
  orgName: string;
  projectName: string;
  projectId: string;
  isDomainVisible: boolean;
  isPublicDomain: boolean;
  isJoiningOrg: boolean;
  joinedOrgId: string;
  joinableOrgs: any[];
  createOrgFailedMsg: string;
  requestedJoinOrgs: number[];
  orgExistsWithIdpEnabled: boolean;
  shouldBypassCongratsScreen: boolean;
  selectedDataResidency: `US` | `EU`;
  executingCreateOrg: boolean;
};

function getLocalStorageKey(type) {
  const userId = new MPContext().userId;
  return `fyt:${userId}:${type}`;
}

const ORG_ALREADY_EXISTS_MSG = `User has already created an organization`;

export class FullScreenSignup extends Component<FullScreenSignupState> {
  DISCOVERABLE_OPEN = `Open`;
  DISCOVERABLE_REQUEST = `Request`;
  DISCOVERABLE_CLOSED = `Closed`;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      next: {type: `string`, description: `next url`},
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        step: FullScreenSignupStepId.JOIN_EXISTING_ORG,
        orgName: ``,
        projectName: ``,
        projectId: ``,
        isDomainVisible: true,
        isPublicDomain: false,
        isJoiningOrg: false,
        joinedOrgId: null,
        joinableOrgs: null,
        createOrgFailedMsg: null,
        requestedJoinOrgs: [],
        orgExistsWithIdpEnabled: false,
        shouldBypassCongratsScreen: false,
        selectedDataResidency: `US` as const,
        executingCreateOrg: false,
      },
      helpers: {
        getBreadcrumbs: () =>
          [
            {
              label: `Create an Organization`,
              selected: this.state.step === FullScreenSignupStepId.CREATE_ORG,
              stepIndex: FullScreenSignupStepId.CREATE_ORG,
            },
            !this.attr(`next`).includes(`startups-apply`) && {
              label: `Implement Mixpanel`,
            },
          ].filter(Boolean),
        handleIsDomainVisibleClick: () => this.update({isDomainVisible: !this.state.isDomainVisible}),
        handleOrgInput: (ev) => this.update({orgName: ev.target.value}),
        goToNextStep: () => {
          if (this.state.step === FullScreenSignupStepId.CREATE_PROJECT) {
            location.href = this.attr(`next`) || `/report/${this.state.projectId}/setup?from_fyt=true`;
            return;
          }

          if (this.state.step + 1 === LAST_STEP && this.state.shouldBypassCongratsScreen) {
            return (location.href = this.helpers.getRedirectLink());
          }
          if (this.state.step + 1 <= LAST_STEP) {
            this.update({step: this.state.step + 1});
          }
        },
        createOrgV2Callback: async () => {
          // from FYT flow, org has not been created yet
          this.update({executingCreateOrg: true});

          const createOrgProjectUrl = `/api/app/organizations/create_org_and_project`;
          try {
            const response = await djangoFetchJson(createOrgProjectUrl, {
              org_name: this.state.orgName,
              org_discoverability: this.state.isDomainVisible ? this.DISCOVERABLE_OPEN : this.DISCOVERABLE_CLOSED,
              cluster_id: this.state.selectedDataResidency === `US` ? 1 : 5,
            });

            if (response.status === `ok`) {
              if (this.attr(`next`)) {
                location.href = this.attr(`next`);
                return;
              }

              const projectId = response?.results[`project_id`]; /* Non-nullish project-id */

              if (projectId) {
                location.href = `/report/${projectId}/setup?from_fyt=true`;
                return;
              }

              throw new Error(`Malformed project id. Please contact support if issue persists.`);
            }
          } catch (err) {
            this.update({executingCreateOrg: false});
            if (err.message === ORG_ALREADY_EXISTS_MSG) {
              addToast({
                type: ToastType.INFO,
                message: `Existing organization found. Redirecting...`,
                timeout: 2000,
                closeToastCallback: () => (location.href = `/report`),
              });
            } else {
              addToast({
                type: ToastType.ERROR,
                isError: true,
                message: err.message,
              });
            }
          }
        },
        handleBreadcrumbClick: (ev) => {
          const {stepIndex} = this.helpers
            .getBreadcrumbs()
            .find((breadcrumb) => breadcrumb.label === ev.detail.node.label);
          this.update({step: stepIndex});
        },
        handleProjectCreate: (ev) => {
          if (!ev.detail.error) {
            mixpanel.track(TrackingEvents.FinishedSetup, {
              'organization name': this.state.orgName,
              'project name': ev.detail.projectName,
              organization_id: ev.detail.orgId,
              project_id: ev.detail.response.id,
            });
            this.update({projectName: ev.detail.projectName, projectId: ev.detail.response.id});
            this.helpers.goToNextStep();
          } else {
            if (ev.detail.error === ORG_ALREADY_EXISTS_MSG) {
              addToast({
                type: ToastType.INFO,
                message: `Existing organization found. Redirecting...`,
                timeout: 2000,
                closeToastCallback: () => (location.href = `/report`),
              });
            } else {
              addToast({
                type: ToastType.ERROR,
                isError: true,
                message: ev.detail.error,
              });
            }
          }
        },
        getRedirectLink: (projectId = null) =>
          this.attr(`next`) || `/report/${projectId || this.state.projectId}/setup`,
        openDemoModal: (ownerEmail) =>
          openDemoProjectSelectorModal(
            (ev) => {
              addToast({message: ev.detail.message, type: ToastType.ERROR});
            },
            {'is-fyt': true, 'owner-email': ownerEmail},
          ),
        joinOrg: async (org) => {
          if (this.state.requestedJoinOrgs.includes(org.id)) {
            // user has already requested to join this org, just show modal
            this.helpers.openDemoModal(org.owner_email);
            return;
          }
          const trackingProps = {
            'Card position': this.state.joinableOrgs.findIndex((aOrg) => aOrg.id === org.id),
            'Number of users': org.num_members,
            'Owner email': org.owner_email,
          };
          this.update({joinedOrgId: org.id, isJoiningOrg: true});

          if (org.discoverable === this.DISCOVERABLE_REQUEST) {
            // request to join, show modal for now
            this.helpers.openDemoModal(org.owner_email);
            mixpanel.track(TrackingEvents.ClickJoinOrg, {...trackingProps, 'Is request': true});
            mixpanel.track(TrackingEvents.PendingInvitePage, {
              organization_id: org.id,
              'organization name': org.name,
            });
          } else {
            mixpanel.track(TrackingEvents.ClickJoinOrg, {...trackingProps, 'Is request': false});
          }

          try {
            if (
              org.discoverable === this.DISCOVERABLE_OPEN ||
              (org.discoverable === this.DISCOVERABLE_REQUEST && !this.state.requestedJoinOrgs.includes(org.id))
            ) {
              await djangoFetchJson(`/api/app/organizations/join_org/`, {
                org_id: org.id,
                project_id: org.project_id,
              });

              if (org.discoverable === this.DISCOVERABLE_OPEN) {
                // they are added to the org/project, redirect them
                location.href = this.helpers.getRedirectLink(org.project_id);
              } else if (org.discoverable === this.DISCOVERABLE_REQUEST) {
                // add this org to list of requested orgs
                this.update({requestedJoinOrgs: [...this.state.requestedJoinOrgs, org.id]});
                window.localStorage.setItem(
                  getLocalStorageKey(`requestedJoinOrgs`),
                  JSON.stringify(this.state.requestedJoinOrgs),
                );
                this.update({isJoiningOrg: false});
              }
            }
          } catch (err) {
            addToast({
              type: ToastType.ERROR,
              isError: true,
              message: err.message,
            });
            this.update({isJoiningOrg: false});
          }
        },
        getUserList: (members): UserList[] => {
          return members.map(({id, name, email, 'img-src': imgSrc}) => {
            if (imgSrc) {
              // if they have an avatar always show it
              return {id, name: name || email, 'img-src': imgSrc};
            }
            if (name) {
              return {id, name};
            } else {
              return {'distinct-id': id, 'tooltip-text': email, type: `tracked-user`};
            }
          });
        },
        getHelpSettingsMenuSections: () => [
          {
            items: [
              {
                label: `Mixpanel Help Center`,
                href: `https://help.mixpanel.com/hc/en-us/categories/115000963103-Manage-Account`,
                target: `_blank`,
                icon: `help-center`,
                tracking: TrackingEvents.MIXPANEL_HELP_SETTINGS_CLICKED,
              },
              {
                label: `Contact Support`,
                href: `https://help.mixpanel.com/hc/en-us/requests/new?ticket_form_id=360000556932`,
                target: `_blank`,
                icon: `email`,
                tracking: TrackingEvents.CONTACT_SUPPORT_CLICKED,
              },
            ],
          },
        ],
        handleHelpSettingsMenuItemSelected: (ev) => {
          const {item} = ev.detail;
          if (item.tracking) {
            mixpanel.track(item.tracking, {
              'Was shown non-IDP org': this.state.orgExistsWithIdpEnabled,
              'Number of orgs': this.state?.joinableOrgs?.length,
            });
          }
        },
      },
    };
  }

  async connectedCallback() {
    super.connectedCallback();
    try {
      // check to see if the user has already tried to request to join some orgs
      const requestedJoinOrgs: any[] =
        JSON.parse(window.localStorage.getItem(getLocalStorageKey(`requestedJoinOrgs`))) || [];
      this.update({requestedJoinOrgs});
    } catch (err) {
      // pass
    }

    if (!this.state.joinableOrgs) {
      try {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {orgs, is_public, org_exists_with_idp_disabled} = await fetchDiscoverableOrgs();
        const showableOrgs = orgs.slice(0, 4);
        mixpanel.track(TrackingEvents.FYTScreen, {'Number of orgs': showableOrgs.length});
        this.update({
          joinableOrgs: showableOrgs, // limit to 4 orgs
          step:
            showableOrgs.length > 0 && !is_public
              ? FullScreenSignupStepId.JOIN_EXISTING_ORG
              : FullScreenSignupStepId.CREATE_ORG,
          isDomainVisible: !is_public,
          isPublicDomain: is_public,
          orgExistsWithIdpEnabled: org_exists_with_idp_disabled,
        });
      } catch (err) {
        this.update({joinableOrgs: [], step: FullScreenSignupStepId.CREATE_ORG});
      }
    }

    if (this.attr(`next`)) {
      this.update({shouldBypassCongratsScreen: true});
    }
  }
}

export const FullScreenSignupEl = defineAndMakeJsxFactory(`mp-full-screen-signup`, FullScreenSignup);
export const FullScreenSignupReact = panel2react(`mp-full-screen-signup`, FullScreenSignup);
